"use client";

// recharts dựng trên React context + DOM nên chỉ chạy được ở client.
// Thiếu directive này thì mọi server component import barrel @chotca/ui đều nổ
// "createContext is not a function" khi Next đánh giá module trên server.
import * as React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartCard } from "./chart-card";
import type { ChartSeries } from "./line-chart-card";
import {
  CHART_AXIS_TICK,
  CHART_GRID_STROKE,
  CHART_TOOLTIP_STYLE,
} from "./chart-theme";

export interface BarChartCardProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  data: Array<Record<string, unknown>>;
  xKey: string;
  series: ChartSeries[];
  height?: number;
  /** Stack all series into one bar instead of grouping side-by-side. */
  stacked?: boolean;
  showLegend?: boolean;
  xTickFormatter?: (value: string) => string;
  valueFormatter?: (value: number) => string;
}

/** Vertical bar chart (x = category/day) wrapped in the shared ChartCard. */
export function BarChartCard({
  title,
  subtitle,
  action,
  data,
  xKey,
  series,
  height = 240,
  stacked = false,
  showLegend = true,
  xTickFormatter,
  valueFormatter,
}: BarChartCardProps) {
  return (
    <ChartCard title={title} subtitle={subtitle} action={action}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: 4, right: 8, bottom: 0, left: -12 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={CHART_GRID_STROKE}
            vertical={false}
          />
          <XAxis
            dataKey={xKey}
            tick={CHART_AXIS_TICK}
            tickFormatter={xTickFormatter}
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis
            tick={CHART_AXIS_TICK}
            tickFormatter={
              valueFormatter ? (v: number) => valueFormatter(v) : undefined
            }
            tickLine={false}
            axisLine={false}
            width={48}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: "rgba(15,15,15,0.04)" }}
            contentStyle={CHART_TOOLTIP_STYLE}
            labelFormatter={(label: unknown) =>
              xTickFormatter ? xTickFormatter(String(label)) : String(label)
            }
            formatter={(value: unknown) =>
              valueFormatter ? valueFormatter(Number(value)) : String(value)
            }
          />
          {showLegend && series.length > 1 ? (
            <Legend wrapperStyle={{ fontSize: 11 }} />
          ) : null}
          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.color}
              radius={stacked ? 0 : [4, 4, 0, 0]}
              stackId={stacked ? "stack" : undefined}
              maxBarSize={48}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
